import convertTime12to24 from '../components/utils/ConvertTime';
import { OrderChannel } from '../components/constants/OrderChannel';
import posOrderIdDB from './indexedDB/posOrderIdDB';
import storeDB from './indexedDB/storeDB';

/**
 * Get station start time from store settings with fallback
 */
export const resolveStationStartTime = async (storeId, providedTime) => {
  if (providedTime) return providedTime;
  if (!storeId) return '04:00';
  try {
    const setting = await storeDB.getStoreSetting(storeId, 'stationStartTime');
    const resolved = setting || '04:00';
    console.log(`[OrderNumber Debug] Resolved StartTime: ${resolved} (from ${setting ? 'DB' : 'fallback'})`);
    return resolved;
  } catch (error) {
    console.log(`[OrderNumber Debug] Resolved StartTime: 04:00 (error fallback)`);
    return '04:00';
  }
};

/**
 * Get business date based on station start time
 * If current time is before station start time, use yesterday's date
 * If current time is at or after station start time, use today's date
 */
export const getBusinessDate = (date, stationStartTime = '04:00') => {
  const now = new Date(date);
  const businessDate = new Date(now);

  // Parse station start time
  const time24 = convertTime12to24(stationStartTime);
  const [hours, minutes] = time24.split(':').map(Number);

  // Set the station start time for today
  businessDate.setHours(hours, minutes, 0, 0);

  // If current time is before station start time, use yesterday
  if (now < businessDate) {
    businessDate.setDate(businessDate.getDate() - 1);
  }

  return businessDate;
};

/**
 * Convert day number (1-31) to base-26 encoding (AA, AB, ..., BE)
 */
const encodeDay = (day) => {
  if (day < 1 || day > 31) {
    throw new Error(`Day must be between 1 and 31, got ${day}`);
  }

  // Day 1-26: AA to AZ (two characters)
  if (day <= 26) {
    const firstChar = 'A'; // Always A for days 1-26
    const secondChar = String.fromCharCode(64 + day); // A=65, so 64+1=65=A, 64+26=90=Z
    return firstChar + secondChar;
  }

  // Day 27-31: BA to BE
  const firstChar = 'B'; // 66
  const secondChar = String.fromCharCode(64 + (day - 26)); // 27->A, 28->B, 29->C, 30->D, 31->E
  return firstChar + secondChar;
};

/**
 * Generate 4-letter date code from business date
 * Format: YMDD (Year, Month, Day-Day)
 * Year: A=2025, B=2026, C=2027, etc.
 * Month: A=January, L=December
 * Day: AA=1, AB=2, ..., AZ=26, BA=27, BB=28, BC=29, BD=30, BE=31
 */
export const generateDateCode = async (date, stationStartTime = null, storeId = null) => {
  const actualStartTime = await resolveStationStartTime(storeId, stationStartTime);
  const businessDate = getBusinessDate(date, actualStartTime);

  const year = businessDate.getFullYear();
  const month = businessDate.getMonth() + 1; // 1-12
  const day = businessDate.getDate(); // 1-31

  // Year: A=2025, B=2026, C=2027, etc.
  const yearCode = String.fromCharCode(65 + (year - 2025)); // A=65

  // Month: A=January (1), L=December (12)
  const monthCode = String.fromCharCode(64 + month); // A=65, so 64+1=65=A

  // Day: AA=1, AB=2, ..., AZ=26, BA=27, BB=28, BC=29, BD=30, BE=31
  const dayCode = encodeDay(day);

  const result = yearCode + monthCode + dayCode;
  console.log(`[Split POS ID Debug] Date Code: ${result} (${businessDate.toLocaleDateString()})`);
  return result;
};

/**
 * Get order channel code
 * C=Counter, T=Takeaway, D=Delivery, O=Online
 * U=Uber, S=Doordash, K=Skip, F=Fantuan (3PL channels)
 */
export const getOrderChannelCode = (orderChannel) => {
  if (!orderChannel) {
    return 'C'; // Default to Counter
  }

  // Check for 3PL orders first (may be in metadata or orderChannel field)
  const orderChannelUpper = orderChannel.toUpperCase();

  // 3PL channels
  if (orderChannelUpper.includes('UBER') || orderChannelUpper === 'UBER') {
    return 'U';
  }
  if (orderChannelUpper.includes('DOORDASH') || orderChannelUpper === 'DOORDASH') {
    return 'S';
  }
  if (orderChannelUpper.includes('SKIP') || orderChannelUpper === 'SKIP') {
    return 'K';
  }
  if (orderChannelUpper.includes('FANTUAN') || orderChannelUpper === 'FANTUAN') {
    return 'F';
  }

  // Regular channels
  switch (orderChannelUpper) {
    case OrderChannel.COUNTER:
    case 'COUNTER':
      return 'C';
    case OrderChannel.TAKEAWAY:
    case 'TAKEAWAY':
      return 'T';
    case OrderChannel.DELIVERY:
    case 'DELIVERY':
    case OrderChannel.DELIVERY_LOCAL:
    case 'DELIVERY_LOCAL':
      return 'D';
    case OrderChannel.ONLINE:
    case 'ONLINE':
      return 'O';
    case OrderChannel.TABLE:
    case 'TABLE':
      return 'T'; // Table service uses Takeaway code
    default:
      return 'C'; // Default to Counter
  }
};

/**
 * Get next sequential number for the given store and date code
 * Uses IndexedDB for offline-first atomic increment
 */
export const getNextSequentialNumber = async (storeId, dateCode, isTrainingMode = false, userId = null) => {
  if (!storeId || !dateCode) {
    throw new Error('storeId and dateCode are required');
  }

  // Initialize database
  await posOrderIdDB.init();

  // Get or create counter for this store/date combination
  // For training mode, use separate key with user ID
  const key = isTrainingMode && userId
    ? `posOrderId_${storeId}_${dateCode}_TRAINING_${userId}`
    : `posOrderId_${storeId}_${dateCode}`;
  const currentCounter = await posOrderIdDB.getCounter(key);

  // Increment counter atomically
  const nextNumber = (currentCounter || 0) + 1;
  await posOrderIdDB.setCounter(key, nextNumber);

  // Format as 4-digit string (0001, 0002, ..., 9999)
  // For training mode, replace first leading zero with 'F'
  let result = String(nextNumber).padStart(4, '0');
  if (isTrainingMode) {
    result = result.replace(/^0/, 'F');
  }
  console.log(`[Split POS ID Debug] Seq Num: ${result} (Training: ${isTrainingMode})`);
  return result;
};

/**
 * Get next sequential number for a specific channel
 * @param {string} storeId - Store ID
 * @param {string} prefix - CS, TW, or DL
 * @param {string} stationStartTime - Station start time
 */
export const getNextChannelCount = async (storeId, prefix, stationStartTime = '04:00') => {
  if (!storeId || !prefix) {
    throw new Error('storeId and prefix are required');
  }

  const dateCode = await generateDateCode(new Date(), stationStartTime, storeId);
  const key = `channelCount_${storeId}_${dateCode}_${prefix}`;

  await posOrderIdDB.init();
  const currentCounter = await posOrderIdDB.getCounter(key);
  const nextNumber = (currentCounter || 0) + 1;
  await posOrderIdDB.setCounter(key, nextNumber);

  return nextNumber;
};

/**
 * Generate complete POS Order ID
 * Format: YMDDCNNN (Year, Month, Day-Day, Channel, Number)
 * Example: AAAAC001 (A=2025, A=Jan, AA=1st, C=Counter, 001=first order)
 */
/**
 * Helper function to check if current user is in training mode
 * @returns {Promise<{isTrainingMode: boolean, userId: string | null}>}
 */
const checkTrainingMode = async () => {
  try {
    // SIMPLE APPROACH: Scan ALL trainingMode_* keys in localStorage
    // Find the first one that is active (not expired)
    // Extract userId from the key name (trainingMode_${userId})
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('trainingMode_') && !key.slice('trainingMode_'.length).includes('_')) {
        try {
          const trainingData = localStorage.getItem(key);
          if (trainingData) {
            const trainingState = JSON.parse(trainingData);
            
            // Check if this training state is active
            if (trainingState.isTrainingMode && trainingState.startTime) {
              const elapsed = Math.floor((Date.now() - trainingState.startTime) / 1000);
              if (elapsed < 5400) { // 90 minutes
                // Extract userId from key (trainingMode_${userId})
                const userId = key.replace('trainingMode_', '');
                console.log(`[Training Mode] posOrderId.checkTrainingMode: ✅ ACTIVE - userId: ${userId}`);
                return { isTrainingMode: true, userId };
              }
            }
          }
        } catch (e) {
          console.error(`[Training Mode] posOrderId.checkTrainingMode: Error parsing key ${key}:`, e);
        }
      }
    }
    
    return { isTrainingMode: false, userId: null };
  } catch (error) {
    console.error('[Training Mode] posOrderId.checkTrainingMode: Fatal error:', error);
    return { isTrainingMode: false, userId: null };
  }
};

/**
 * Generate POS Order ID
 * @param {string} storeId - Store ID
 * @param {string} orderChannel - Order channel (COUNTER, TAKEAWAY, DELIVERY)
 * @param {string} stationStartTime - Station start time (optional)
 */
export const generatePosOrderId = async (storeId, orderChannel, stationStartTime = null) => {
  if (!storeId) {
    throw new Error('storeId is required');
  }

  // Check training mode internally
  const { isTrainingMode, userId } = await checkTrainingMode();

  // Generate date code based on business date
  const dateCode = await generateDateCode(new Date(), stationStartTime, storeId);

  // Get channel code
  const channelCode = getOrderChannelCode(orderChannel);

  // Get next sequential number (with training mode support)
  const sequentialNumber = await getNextSequentialNumber(storeId, dateCode, isTrainingMode, userId);

  // Combine: YMDD + Channel + Number
  const result = dateCode + channelCode + sequentialNumber;
  console.log(`[Split POS ID Debug] Generated: ${result} (Channel: ${orderChannel}, Training: ${isTrainingMode})`);
  return result;
};


export const prunePosOrderId = async (id, storeId) => {
  if (!id || !storeId) return;

  try {
    // Extract date code (first 4 characters)
    const dateCode = id.substring(0, 4);
    // Extract sequential number (last 3 characters)
    const seqPart = id.substring(id.length - 3);
    const seqNumber = parseInt(seqPart, 10);

    if (isNaN(seqNumber)) return;

    const key = `posOrderId_${storeId}_${dateCode}`;
    await posOrderIdDB.decrementCounter(key, seqNumber);
  } catch (error) {
    console.warn('[PosOrderId] Error pruning ID:', id, error);
  }
};

export default {
  getBusinessDate,
  generateDateCode,
  getOrderChannelCode,
  getNextSequentialNumber,
  getNextChannelCount,
  generatePosOrderId,
  prunePosOrderId,
};


